/**
 * HERO SLIDESHOW GIF (2026-09-16, Jack: "i want to create a gif like we did on the html").
 *
 * The monolith's `__mediaBuilder` (index.html L18220): 2–5 Bynder photos → one animated GIF
 * that becomes the hero image, every photo cover-fitted to the slot. It was cut from the MVP
 * on 08-07 and never made the port; this is that engine on the Hub's `encodeGif`.
 *
 * Rules carried over:
 *  - Bynder only (2026-07-25, Jack: "never upload"). The frames are Bynder URLs; their bytes come
 *    through the backend's `/bynder-img` proxy because a cross-origin <img> taints the canvas.
 *  - The GIF is rendered at the SLOT's pixels (the hero declares 500×350 / 600×350) — a hero GIF
 *    ships as-is (no transform), so its size is decided here, once, and it stays small.
 *  - The result is a data URL in the block's image field. It is a PREVIEW until hosted: the
 *    submit path uploads it to SFMC Content Builder and swaps in SFMC's URL (services/hostImage.ts).
 *  - Outlook on Windows shows the FIRST frame only — so frame 1 is the photo that must carry it.
 */
import { encodeGif } from "./gif";
import { bynderImageBytes } from "./api";

export interface HeroGifSpec { imgs: string[]; delay: number; /** each frame's own pixels ("2000x3000"), for the per-frame drag + zoom (2026-09-16) */ nats?: string[] }

export const HERO_GIF_MIN = 2;
export const HERO_GIF_MAX = 5;
/**
 * ONE SPEED: 2 s PER PHOTO (stand-up 2026-09-17). Justin builds his at two-second
 * intervals; divisions flagged 1.5 s as too fast to take a photo in. The picker is gone —
 * the builder writes 2000 into the spec and a reopened GIF keeps whatever it was built at.
 */
export const HERO_GIF_DEFAULT_DELAY = 2000;

/** the sidecar the builder writes beside the image so the GIF can be reopened and rebuilt */
export function heroGifSpecOf(raw: unknown): HeroGifSpec | null {
  try {
    const j = JSON.parse(String(raw || ""));
    if (!j || !Array.isArray(j.imgs)) return null;
    const imgs = j.imgs.map((s: unknown) => String(s || "")).filter(Boolean);
    const delay = Number(j.delay) || HERO_GIF_DEFAULT_DELAY;
    const nats = Array.isArray(j.nats) ? j.nats.map((s: unknown) => String(s || "")) : undefined;
    return { imgs, delay, ...(nats ? { nats } : {}) };
  } catch { return null; }
}
export function heroGifSpecStr(s: HeroGifSpec): string { return JSON.stringify({ imgs: s.imgs, delay: s.delay, ...(s.nats ? { nats: s.nats } : {}) }); }

/**
 * The photo's bytes: the backend proxy first, Bynder directly when the proxy is not there.
 *
 * WHY THE FALLBACK (dev, 2026-09-17, Jack: "it's happening now"): on emailbuilder-dev every
 * backend call goes through the AWS API Gateway, and `GET /bynder-img` was never registered
 * there ("Missing Authentication Token" = no such route; the preflight then fails and the
 * browser reports "Failed to fetch"). Every frame dropped, the builder said "Could not load
 * enough of the photos", and QA — same-origin proxy — never showed it. Bynder's transform CDN
 * answers a cross-origin fetch (measured from the QA page), so when the proxy fails the frame
 * comes straight from Bynder as a Blob — a Blob is same-origin, the canvas stays untainted.
 * The proxy remains first: it is the path that also covers hosts Bynder would refuse.
 */
export async function frameBytes(url: string): Promise<Uint8Array> {
  try {
    return await bynderImageBytes(url);
  } catch (proxyErr) {
    try {
      const r = await fetch(url, { mode: "cors", cache: "no-store" });
      if (!r.ok) throw new Error("Bynder → " + r.status);
      return new Uint8Array(await r.arrayBuffer());
    } catch (directErr) {
      throw new Error(String((proxyErr as Error)?.message || proxyErr) + "; " + String((directErr as Error)?.message || directErr));
    }
  }
}

/** load one frame's pixels as an <img> the canvas may read (same-origin blob) */
export async function loadFrame(url: string): Promise<HTMLImageElement> {
  const bytes = await frameBytes(url);
  const blob = new Blob([bytes as BlobPart]);
  const obj = URL.createObjectURL(blob);
  try {
    return await new Promise<HTMLImageElement>((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = () => rej(new Error("could not decode " + url));
      im.src = obj;
    });
  } finally {
    /* the Image holds its own decode; the object URL can go once loaded (or failed) */
    setTimeout(() => { try { URL.revokeObjectURL(obj); } catch { /* gone */ } }, 0);
  }
}

/** draw `im` cover-fitted and centred into W×H — the same fill the email's slot does */
export function paintCover(ctx: CanvasRenderingContext2D, im: { width: number; height: number } & CanvasImageSource, W: number, H: number): void {
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, W, H);
  if (!im.width || !im.height) return;
  const r = Math.max(W / im.width, H / im.height);
  const w = im.width * r, h = im.height * r;
  ctx.drawImage(im, (W - w) / 2, (H - h) / 2, w, h);
}

/**
 * Build the slideshow GIF. Resolves to a `data:image/gif;base64,…` URL sized W×H.
 * Frames that fail to load are dropped (the caller sees the count in `frames`).
 */
export async function buildHeroGif(spec: HeroGifSpec, W: number, H: number, onProgress?: (pct: number) => void, load: (u: string) => Promise<HTMLImageElement> = loadFrame): Promise<{ dataUrl: string; frames: number }> {
  const imgs = spec.imgs.slice(0, HERO_GIF_MAX);
  if (imgs.length < HERO_GIF_MIN) throw new Error("Add at least " + HERO_GIF_MIN + " photos.");
  const els: HTMLImageElement[] = [];
  const failed: string[] = [];
  for (let i = 0; i < imgs.length; i++) {
    try { els.push(await load(imgs[i])); } catch (e) { failed.push("photo " + (i + 1) + " (" + String((e as Error)?.message || e).slice(0, 120) + ")"); }
  }
  /* the message names the photos and the reason — "could not load enough" hid a dead route for a day */
  if (els.length < HERO_GIF_MIN) throw new Error("Could not load " + failed.join(", ") + ".");
  const dataUrl = await encodeGif(W, H, els.length, spec.delay, (ctx, f) => paintCover(ctx, els[f], W, H), onProgress, 1);
  return { dataUrl, frames: els.length };
}

/** the bytes of a data: URL (for the frame count under the card) */
export function dataUrlBytes(u: string): Uint8Array | null {
  const m = /^data:[^;,]+;base64,(.*)$/i.exec(String(u || ""));
  if (!m) return null;
  try {
    const bin = atob(m[1]);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch { return null; }
}
export function isDataGif(u: unknown): boolean { return /^data:image\/gif[;,]/i.test(String(u || "")); }
